import type { ReactNode } from 'react';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import type { DetailSection } from '@/shared/app-detail-layout/types';
import type { AsyncResource } from '@/shared/hooks/use-async-resource';
import type { SelectOption } from '@/shared/ui/select';
import type { EffectiveAiSettings } from '@/api/schemas/ai';
import type { Category } from '@/api/schemas/catalog-taxonomy';
import { AiAvailabilityPanel, InstructionsField } from '@/modules/catalog/pages/setup-page/parts/assistant-panel';
import { CategoryPanel } from '@/modules/catalog/pages/setup-page/parts/category-panel';
import type { CategoryPanelProps } from '@/modules/catalog/pages/setup-page/parts/category-panel';
import { AttributePanel } from '@/modules/catalog/pages/setup-page/parts/attribute-panel';
import type { AttributePanelProps } from '@/modules/catalog/pages/setup-page/parts/attribute-panel';
import { parentCategoryOptions } from '@/modules/catalog/pages/setup-page/parts/options';
import type { SetupController } from '@/modules/catalog/pages/setup-page/parts/use-setup';

/**
 * The page's frame.
 *
 * The layout owns the frame, the header, the tab strip and the state surfaces.
 * This file owns the ORDER of the journey and nothing else: describe, ask,
 * review, accept, save.
 */
export type SetupViewProps = {
  readonly setup: SetupController;
  readonly ai: AsyncResource<EffectiveAiSettings>;
  readonly categories: AsyncResource<ReadonlyArray<Category>>;
  /** Why the existing attribute dictionary could not be read, when it could not. */
  readonly dictionaryError: string | null;
  readonly onRetryDictionary: () => void;
};

const TABS = [
  { id: 'categories', label: 'Categories' },
  { id: 'attributes', label: 'Attributes' },
];

export function SetupView({
  setup,
  ai,
  categories,
  dictionaryError,
  onRetryDictionary,
}: SetupViewProps): ReactNode {
  const list = categories.data ?? [];

  return (
    <AppDetailLayout
      title="Catalogue setup"
      subtitle="Describe what you sell, review what the assistant proposes, and keep what you want."
      toolbar={<SetupToolbar setup={setup} ai={ai} />}
      tabs={TABS}
      sections={buildSections(setup, list, dictionaryError, onRetryDictionary)}
      tabsInHeader
    />
  );
}

/**
 * What the page says before it offers anything.
 *
 * The availability comes first, because a merchant should know whether the
 * assistant can work BEFORE they are asked to describe their catalogue.
 */
function SetupToolbar({
  setup,
  ai,
}: {
  readonly setup: SetupController;
  readonly ai: AsyncResource<EffectiveAiSettings>;
}): ReactNode {
  return (
    <div className="space-y-4">
      <p className="text-sm text-content-muted">
        The assistant proposes a category tree and the attributes that go with it. It never writes
        anything: everything it suggests is shown to you first, and saved through the ordinary
        catalogue API.
      </p>
      <AiAvailabilityPanel
        settings={ai.data}
        error={ai.error === null ? null : ai.error.message}
        onRetry={ai.reload}
      />
      <InstructionsField
        value={setup.state.instructions}
        onChange={setup.setInstructions}
      />
    </div>
  );
}

function buildSections(
  setup: SetupController,
  categories: ReadonlyArray<Category>,
  dictionaryError: string | null,
  onRetryDictionary: () => void,
): ReadonlyArray<DetailSection> {
  return [
    {
      id: 'categories',
      label: 'Categories',
      content: <CategoryPanel {...categoryPanelProps(setup, categories)} />,
    },
    {
      id: 'attributes',
      label: 'Attributes',
      content: (
        <AttributePanel
          {...attributePanelProps(setup, categories, dictionaryError, onRetryDictionary)}
        />
      ),
    },
  ];
}

function categoryPanelProps(
  setup: SetupController,
  categories: ReadonlyArray<Category>,
): CategoryPanelProps {
  const state = setup.state;
  return {
    drafts: state.categoryDrafts,
    proposal: state.categoryProposal,
    existingCategories: categories,
    parentOptions: parentCategoryOptions(categories, state.createdCategories),
    canSuggest: setup.canSuggest,
    isWorking: state.isWorking,
    failure: state.categoryFailure,
    manualOutcome: state.manualCategoryOutcome,
    onSuggest: setup.suggestCategories,
    onChange: setup.updateCategory,
    onCreateSelected: setup.createSelectedCategories,
    onManualCreate: setup.createManualCategory,
  };
}

function attributePanelProps(
  setup: SetupController,
  categories: ReadonlyArray<Category>,
  dictionaryError: string | null,
  onRetryDictionary: () => void,
): AttributePanelProps {
  const state = setup.state;
  return {
    drafts: state.attributeDrafts,
    proposal: state.attributeProposal,
    targetOptions: targetOptions(setup, categories),
    targetCategoryId: state.targetCategoryId,
    canSuggest: setup.canSuggest,
    isWorking: state.isWorking,
    failure: state.attributeFailure,
    manualOutcome: state.manualAttributeOutcome,
    dictionaryError,
    onRetryDictionary,
    onTargetChange: setup.setTargetCategoryId,
    onSuggest: setup.suggestAttributes,
    onChange: setup.updateAttribute,
    onCreateSelected: setup.createSelectedAttributes,
    onManualCreate: setup.createManualAttribute,
  };
}

/** The categories the attributes can be for. */
function targetOptions(
  setup: SetupController,
  categories: ReadonlyArray<Category>,
): SelectOption[] {
  return [
    { value: '', label: 'No category — the definitions stand on their own' },
    ...setup.state.createdCategories.map((category) => ({
      value: category.categoryId,
      label: `${category.name} (created here)`,
    })),
    ...categories.map((category) => ({ value: category.categoryId, label: category.name })),
  ];
}
